import React, { useState, useEffect } from 'react';
import {
  Mail,
  Send,
  Edit3,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  Monitor,
  Copy,
  Plus,
  Trash2,
  RefreshCw,
  Sliders,
  ShieldCheck,
  ChevronRight,
  ArrowLeft,
  Sparkles,
  Search,
  ExternalLink,
  Code,
  Eye,
  Info,
  Layers,
  Users
} from 'lucide-react';
import {
  EmailTemplate,
  RecipientRole,
  DEFAULT_EMAIL_TEMPLATES,
  getEmailTemplates,
  saveEmailTemplate,
  sendLiveTestEmail,
  substituteMergeTags,
  buildFullEmailHtml,
} from '../services/emailService';
import { useAuth } from '../context/AuthContext';

const ROLE_LABELS: Record<RecipientRole, { label: string; bg: string; text: string; border: string; icon: string }> = {
  buyer: {
    label: 'Buyer',
    bg: 'bg-blue-500/15',
    text: 'text-blue-300',
    border: 'border-blue-500/30',
    icon: '👤',
  },
  seller: {
    label: 'Vendor / Seller',
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-300',
    border: 'border-emerald-500/30',
    icon: '🏪',
  },
  super_admin: {
    label: 'Super Admin',
    bg: 'bg-purple-500/15',
    text: 'text-purple-300',
    border: 'border-purple-500/30',
    icon: '🛡️',
  },
  mediator: {
    label: 'Dispute Mediator',
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/30',
    icon: '⚖️',
  },
  support_agent: {
    label: 'Support Desk',
    bg: 'bg-teal-500/15',
    text: 'text-teal-300',
    border: 'border-teal-500/30',
    icon: '🎧',
  },
  user: {
    label: 'General Customer',
    bg: 'bg-sky-500/15',
    text: 'text-sky-300',
    border: 'border-sky-500/30',
    icon: '👥',
  },
};

const VERIFIED_ZEPTO_SENDERS = [
  { address: 'orders@mail.reuseit.shop', name: 'ReUseIt Escrow & Logistics', purpose: 'Escrow funded, tracking, inspections & vendor payouts' },
  { address: 'security@mail.reuseit.shop', name: 'ReUseIt Security Operations', purpose: 'Admin 2FA OTPs, auth passcodes & password resets' },
  { address: 'disputes@mail.reuseit.shop', name: 'ReUseIt Mediation Tribunal', purpose: 'Dispute freezes, arbitration hearings & binding verdicts' },
  { address: 'onboarding@mail.reuseit.shop', name: 'ReUseIt Team Operations', purpose: 'Staff onboarding invitations & provisional credentials' },
  { address: 'support@mail.reuseit.shop', name: 'ReUseIt Customer Care', purpose: 'Helpdesk responses & customer inquiries' },
  { address: 'notifications@mail.reuseit.shop', name: 'ReUseIt Trust & Safety', purpose: 'Vendor KYC verification outcomes & broadcast alerts' },
];

export const EmailTemplatesView: React.FC = () => {
  const { currentUser } = useAuth();
  const [templates, setTemplates] = useState<EmailTemplate[]>(DEFAULT_EMAIL_TEMPLATES);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Test Email Modal State
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState(currentUser?.email || 'oliversammytwist@gmail.com');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; messageId?: string; error?: string } | null>(null);

  // Editor View Sub-states
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [editorTab, setEditorTab] = useState<'visual' | 'html'>('visual');
  const [copiedTag, setCopiedTag] = useState<string | null>(null);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [newBccEmail, setNewBccEmail] = useState('');
  const [showSenderGuide, setShowSenderGuide] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getEmailTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed loading email templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (templateId: string, currentEnabled: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = templates.map((t) => (t.id === templateId ? { ...t, enabled: !currentEnabled } : t));
    setTemplates(updated);
    const target = updated.find((t) => t.id === templateId);
    if (target) {
      try {
        await saveEmailTemplate(target, currentUser?.email || 'superadmin@reuseit.shop');
        showToast('success', `${target.name} is now ${!currentEnabled ? 'Enabled' : 'Disabled'}.`);
      } catch (err) {
        showToast('error', 'Failed to update template state in Firestore.');
      }
    }
  };

  const handleSaveSelected = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      await saveEmailTemplate(selectedTemplate, currentUser?.email || 'superadmin@reuseit.shop');
      setTemplates((prev) => prev.map((t) => (t.id === selectedTemplate.id ? selectedTemplate : t)));
      showToast('success', `"${selectedTemplate.name}" configuration saved successfully.`);
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to save changes to Firestore.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedTemplate || !testEmailAddress) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await sendLiveTestEmail(selectedTemplate, testEmailAddress);
      setTestResult(res);
      if (res.success) {
        showToast('success', `Test email dispatched to ${testEmailAddress} via ZeptoMail!`);
      } else {
        showToast('error', res.error || 'Failed to dispatch test email.');
      }
    } catch (err: any) {
      setTestResult({ success: false, error: err.message || 'Transmission failed.' });
      showToast('error', 'Transmission failed.');
    } finally {
      setSendingTest(false);
    }
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4500);
  };

  const copyToClipboard = (tag: string) => {
    navigator.clipboard.writeText(tag);
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(null), 1800);
  };

  const toggleRecipientRole = (role: RecipientRole) => {
    if (!selectedTemplate) return;
    const exists = selectedTemplate.recipientRoles.includes(role);
    const updatedRoles = exists
      ? selectedTemplate.recipientRoles.filter((r) => r !== role)
      : [...selectedTemplate.recipientRoles, role];
    setSelectedTemplate({ ...selectedTemplate, recipientRoles: updatedRoles });
  };

  const addCustomCc = () => {
    if (!selectedTemplate || !newCcEmail.trim() || !newCcEmail.includes('@')) return;
    const current = selectedTemplate.customCc || [];
    if (!current.includes(newCcEmail.trim())) {
      setSelectedTemplate({ ...selectedTemplate, customCc: [...current, newCcEmail.trim()] });
    }
    setNewCcEmail('');
  };

  const removeCustomCc = (emailToRemove: string) => {
    if (!selectedTemplate) return;
    setSelectedTemplate({
      ...selectedTemplate,
      customCc: (selectedTemplate.customCc || []).filter((e) => e !== emailToRemove),
    });
  };

  const addCustomBcc = () => {
    if (!selectedTemplate || !newBccEmail.trim() || !newBccEmail.includes('@')) return;
    const current = selectedTemplate.customBcc || [];
    if (!current.includes(newBccEmail.trim())) {
      setSelectedTemplate({ ...selectedTemplate, customBcc: [...current, newBccEmail.trim()] });
    }
    setNewBccEmail('');
  };

  const removeCustomBcc = (emailToRemove: string) => {
    if (!selectedTemplate) return;
    setSelectedTemplate({
      ...selectedTemplate,
      customBcc: (selectedTemplate.customBcc || []).filter((e) => e !== emailToRemove),
    });
  };

  // Filter templates
  const categories = [
    'all',
    'Onboarding & Lifecycle',
    'Escrow & Orders',
    'Disputes & Arbitration',
    'Security & Auth',
    'Governance & Staff',
    'Trust & KYC',
    'Support & Helpdesk'
  ];
  const filteredTemplates = templates.filter((t) => {
    const matchesCategory = activeCategory === 'all' || t.category === activeCategory;
    const matchesSearch =
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.senderAddress.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Render preview HTML with sample values
  const getRenderedPreviewHtml = (tmpl: EmailTemplate) => {
    const sampleValues: Record<string, string> = {};
    tmpl.availableMergeTags.forEach((m) => {
      sampleValues[m.tag] = m.sample;
    });
    const subSubject = substituteMergeTags(tmpl.subject, sampleValues);
    const subHeading = substituteMergeTags(tmpl.heading, sampleValues);
    const subPreheader = substituteMergeTags(tmpl.preheader, sampleValues);
    const subBody = substituteMergeTags(tmpl.bodyHtml, sampleValues);
    return buildFullEmailHtml(subHeading, subPreheader, subBody, tmpl.senderAddress.split('@')[1] || 'mail.reuseit.shop');
  };

  return (
    <div className="space-y-8 pb-16">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border transition-all animate-slideUp ${
            notification.type === 'success'
              ? 'bg-[#102a20] border-[#0df2a6]/50 text-white'
              : 'bg-[#2a1010] border-red-500/50 text-white'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-[#0df2a6]" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-400" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
        </div>
      )}

      {/* TOP HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black tracking-tight text-white dark:text-white light:text-[#0D1C17]">
              Transactional Email Settings
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></span>
              Zoho ZeptoMail SMTP
            </span>
          </div>
          <p className="text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
            Notifications manager for order escrows, dispatch logistics, 48-hr inspections, disputes, and admin governance.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSenderGuide(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#152a23] hover:bg-[#1c382f] text-[#0df2a6] border border-[#22493c] transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            ZeptoMail Senders Guide
          </button>
          <button
            onClick={loadTemplates}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-[#152a23] hover:bg-[#1c382f] text-slate-300 border border-[#22493c] transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Sync Templates
          </button>
        </div>
      </div>

      {/* STATS OVERVIEW CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0df2a6]/10 border border-[#0df2a6]/20 flex items-center justify-center text-[#0df2a6]">
            <Mail className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-black text-white">{templates.length}</div>
            <div className="text-xs text-[#90cbb7] font-medium">Automated Templates</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl font-black text-white">
              {templates.filter((t) => t.enabled).length} / {templates.length}
            </div>
            <div className="text-xs text-[#90cbb7] font-medium">Active & Automated</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
            <Send className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white truncate">smtp.zeptomail.com</div>
            <div className="text-xs text-[#90cbb7] font-medium">Port 465 (SSL Encrypted)</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-white truncate">mail.reuseit.shop</div>
            <div className="text-xs text-[#90cbb7] font-medium">Verified Subdomain</div>
          </div>
        </div>
      </div>

      {/* MAIN VIEW: LIST VS EDITOR */}
      {!selectedTemplate ? (
        /* ========================================================================= */
        /* TEMPLATE CATALOG (WOOCOMMERCE EMAILS TABLE)                                */
        /* ========================================================================= */
        <div className="space-y-6">
          {/* Filter & Search Bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Category Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    activeCategory === cat
                      ? 'bg-[#0df2a6] text-[#08130f] shadow-sm'
                      : 'bg-[#152a23] text-[#90cbb7] hover:bg-[#1c382f] border border-[#22493c]'
                  }`}
                >
                  {cat === 'all' ? 'All Templates' : cat}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search templates, subjects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 bg-[#10231d] border border-[#22493c] rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-[#0df2a6]"
              />
            </div>
          </div>

          {/* WooCommerce-Style Email Table */}
          <div className="bg-[#10231d] border border-[#22493c] rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#22493c] bg-[#152a23]/60 text-xs font-bold text-[#90cbb7] uppercase tracking-wider">
                    <th className="py-3.5 px-4 w-16 text-center">Status</th>
                    <th className="py-3.5 px-4">Email Notification</th>
                    <th className="py-3.5 px-4">Category</th>
                    <th className="py-3.5 px-4">Recipient(s)</th>
                    <th className="py-3.5 px-4">Sender Address</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22493c]/60 text-sm">
                  {filteredTemplates.map((template) => (
                    <tr
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className="hover:bg-[#152a23]/50 transition-colors cursor-pointer group"
                    >
                      {/* Active Toggle Switch */}
                      <td className="py-4 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => handleToggleEnabled(template.id, template.enabled, e)}
                          title={template.enabled ? 'Click to disable' : 'Click to enable'}
                          className={`w-10 h-5 rounded-full p-0.5 transition-colors relative inline-block ${
                            template.enabled ? 'bg-[#0df2a6]' : 'bg-slate-700'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform ${
                              template.enabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>

                      {/* Email Name & Description */}
                      <td className="py-4 px-4">
                        <div className="font-bold text-white group-hover:text-[#0df2a6] transition-colors flex items-center gap-2">
                          {template.name}
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-1 max-w-md">
                          {template.description}
                        </p>
                      </td>

                      {/* Category Badge */}
                      <td className="py-4 px-4">
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[#152a23] text-[#90cbb7] border border-[#22493c] whitespace-nowrap">
                          {template.category}
                        </span>
                      </td>

                      {/* Recipient Chips (WooCommerce style) */}
                      <td className="py-4 px-4">
                        <div className="flex flex-wrap gap-1.5 max-w-xs">
                          {template.recipientRoles.map((role) => {
                            const info = ROLE_LABELS[role] || {
                              label: role,
                              bg: 'bg-slate-500/20',
                              text: 'text-slate-300',
                              border: 'border-slate-500/30',
                              icon: '📧',
                            };
                            return (
                              <span
                                key={role}
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${info.bg} ${info.text} ${info.border}`}
                              >
                                <span>{info.icon}</span>
                                {info.label}
                              </span>
                            );
                          })}
                          {(template.customCc?.length || 0) > 0 && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                              +{template.customCc?.length} CC
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Sender */}
                      <td className="py-4 px-4">
                        <div className="text-xs font-semibold text-slate-200">{template.senderName}</div>
                        <div className="text-[11px] font-mono text-[#0df2a6]/80">{template.senderAddress}</div>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedTemplate(template);
                              setShowTestModal(true);
                            }}
                            title="Quick Test"
                            className="p-1.5 rounded-lg bg-[#152a23] hover:bg-[#1c382f] text-[#0df2a6] border border-[#22493c] transition-colors"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setSelectedTemplate(template)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#152a23] hover:bg-[#0df2a6] hover:text-[#08130f] text-white border border-[#22493c] transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Manage
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredTemplates.length === 0 && (
              <div className="p-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <h3 className="text-base font-bold text-white">No Email Templates Found</h3>
                <p className="text-xs text-slate-400 mt-1">Try adjusting your category filter or search terms.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ========================================================================= */
        /* WOOCOMMERCE-STYLE EMAIL TEMPLATE EDITOR                                    */
        /* ========================================================================= */
        <div className="space-y-6 animate-fadeIn">
          {/* Editor Header Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-2xl bg-[#10231d] border border-[#22493c]">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedTemplate(null);
                  loadTemplates();
                }}
                className="p-2 rounded-xl bg-[#152a23] hover:bg-[#1c382f] text-slate-300 border border-[#22493c] transition-colors"
                title="Back to all email templates"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-black text-white">{selectedTemplate.name}</h2>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-[#152a23] text-[#0df2a6] border border-[#22493c]">
                    {selectedTemplate.category}
                  </span>
                </div>
                <p className="text-xs text-slate-400">{selectedTemplate.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 self-end sm:self-auto">
              <div className="flex items-center gap-2 mr-2">
                <span className="text-xs text-slate-400 font-medium">Notification:</span>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedTemplate({ ...selectedTemplate, enabled: !selectedTemplate.enabled })
                  }
                  className={`w-10 h-5 rounded-full p-0.5 transition-colors relative inline-block ${
                    selectedTemplate.enabled ? 'bg-[#0df2a6]' : 'bg-slate-700'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white transition-transform ${
                      selectedTemplate.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-xs font-bold text-white">
                  {selectedTemplate.enabled ? 'Active' : 'Disabled'}
                </span>
              </div>

              <button
                onClick={() => setShowTestModal(true)}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-[#152a23] hover:bg-[#1c382f] text-[#0df2a6] border border-[#22493c] transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                Send Test Email
              </button>

              <button
                onClick={handleSaveSelected}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold bg-[#0df2a6] hover:bg-[#0bd390] text-[#08130f] transition-colors shadow-glow-sm"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>

          {/* Editor 2-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Settings, Routing & Body Editor (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              {/* 1. Sender & Recipient Routing Card */}
              <div className="p-6 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-5">
                <div className="flex items-center gap-2 border-b border-[#22493c] pb-3">
                  <Users className="w-4 h-4 text-[#0df2a6]" />
                  <h3 className="text-sm font-black text-white uppercase tracking-wider">
                    Recipient Routing & Sender Identity
                  </h3>
                </div>

                {/* Recipient Roles Selection (WooCommerce style) */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-2">
                    Recipient Target Audience (Who receives this notification?)
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(Object.keys(ROLE_LABELS) as RecipientRole[]).map((role) => {
                      const isSelected = selectedTemplate.recipientRoles.includes(role);
                      const roleInfo = ROLE_LABELS[role];
                      return (
                        <button
                          key={role}
                          type="button"
                          onClick={() => toggleRecipientRole(role)}
                          className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-semibold text-left transition-all ${
                            isSelected
                              ? 'bg-[#152a23] border-[#0df2a6] text-white shadow-sm'
                              : 'bg-[#10231d] border-[#22493c] text-slate-400 hover:border-slate-500'
                          }`}
                        >
                          <span className="text-base">{roleInfo.icon}</span>
                          <span className="flex-1 truncate">{roleInfo.label}</span>
                          {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-[#0df2a6] shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sender Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">
                      "From" Display Name
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.senderName}
                      onChange={(e) =>
                        setSelectedTemplate({ ...selectedTemplate, senderName: e.target.value })
                      }
                      className="w-full px-3.5 py-2 bg-[#152a23] border border-[#22493c] rounded-xl text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">
                      "From" Sender Address (mail.reuseit.shop)
                    </label>
                    <select
                      value={selectedTemplate.senderAddress}
                      onChange={(e) =>
                        setSelectedTemplate({ ...selectedTemplate, senderAddress: e.target.value })
                      }
                      className="w-full px-3.5 py-2 bg-[#152a23] border border-[#22493c] rounded-xl text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                    >
                      {VERIFIED_ZEPTO_SENDERS.map((s) => (
                        <option key={s.address} value={s.address}>
                          {s.address} ({s.name})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    Reply-To Address
                  </label>
                  <input
                    type="email"
                    value={selectedTemplate.replyTo}
                    onChange={(e) =>
                      setSelectedTemplate({ ...selectedTemplate, replyTo: e.target.value })
                    }
                    className="w-full px-3.5 py-2 bg-[#152a23] border border-[#22493c] rounded-xl text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                  />
                </div>

                {/* Custom CC and BCC Notification Addresses */}
                <div className="pt-2 border-t border-[#22493c]/60 space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">
                      Custom CC Notification Addresses (Optional)
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="email"
                        placeholder="e.g. audit@reuseit.shop"
                        value={newCcEmail}
                        onChange={(e) => setNewCcEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomCc())}
                        className="flex-1 px-3 py-1.5 bg-[#152a23] border border-[#22493c] rounded-lg text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                      />
                      <button
                        type="button"
                        onClick={addCustomCc}
                        className="px-3 py-1.5 bg-[#152a23] hover:bg-[#1c382f] text-[#0df2a6] border border-[#22493c] rounded-lg text-xs font-bold flex items-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add CC
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(selectedTemplate.customCc || []).map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#152a23] text-slate-300 border border-[#22493c] text-xs"
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => removeCustomCc(email)}
                            className="hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">
                      Custom BCC Addresses (Compliance & Archival)
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="email"
                        placeholder="e.g. compliance@reuseit.shop"
                        value={newBccEmail}
                        onChange={(e) => setNewBccEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomBcc())}
                        className="flex-1 px-3 py-1.5 bg-[#152a23] border border-[#22493c] rounded-lg text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                      />
                      <button
                        type="button"
                        onClick={addCustomBcc}
                        className="px-3 py-1.5 bg-[#152a23] hover:bg-[#1c382f] text-[#0df2a6] border border-[#22493c] rounded-lg text-xs font-bold flex items-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add BCC
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(selectedTemplate.customBcc || []).map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#152a23] text-slate-300 border border-[#22493c] text-xs"
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => removeCustomBcc(email)}
                            className="hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Subject & Content Details Card */}
              <div className="p-6 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-5">
                <div className="flex items-center gap-2 border-b border-[#22493c] pb-3">
                  <Mail className="w-4 h-4 text-[#0df2a6]" />
                  <h3 className="text-sm font-black text-white uppercase tracking-wider">
                    Subject Line & Preheader
                  </h3>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    Subject Line (Supports Merge Tags)
                  </label>
                  <input
                    type="text"
                    value={selectedTemplate.subject}
                    onChange={(e) =>
                      setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })
                    }
                    className="w-full px-3.5 py-2.5 bg-[#152a23] border border-[#22493c] rounded-xl text-sm text-white font-medium focus:outline-none focus:border-[#0df2a6]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">
                      Email Heading (H1 Inside Email)
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.heading}
                      onChange={(e) =>
                        setSelectedTemplate({ ...selectedTemplate, heading: e.target.value })
                      }
                      className="w-full px-3.5 py-2 bg-[#152a23] border border-[#22493c] rounded-xl text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1.5">
                      Preheader (Inbox Snippet Text)
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.preheader}
                      onChange={(e) =>
                        setSelectedTemplate({ ...selectedTemplate, preheader: e.target.value })
                      }
                      className="w-full px-3.5 py-2 bg-[#152a23] border border-[#22493c] rounded-xl text-xs text-white focus:outline-none focus:border-[#0df2a6]"
                    />
                  </div>
                </div>
              </div>

              {/* 3. Merge Tags Picker & Body Template Editor */}
              <div className="p-6 rounded-2xl bg-[#10231d] border border-[#22493c] space-y-4">
                <div className="flex items-center justify-between border-b border-[#22493c] pb-3">
                  <div className="flex items-center gap-2">
                    <Code className="w-4 h-4 text-[#0df2a6]" />
                    <h3 className="text-sm font-black text-white uppercase tracking-wider">
                      Email Body Content & Merge Tags
                    </h3>
                  </div>

                  <div className="flex items-center gap-1 bg-[#152a23] p-0.5 rounded-lg border border-[#22493c]">
                    <button
                      type="button"
                      onClick={() => setEditorTab('visual')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        editorTab === 'visual' ? 'bg-[#0df2a6] text-[#08130f]' : 'text-slate-400'
                      }`}
                    >
                      Formatted View
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorTab('html')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                        editorTab === 'html' ? 'bg-[#0df2a6] text-[#08130f]' : 'text-slate-400'
                      }`}
                    >
                      Raw HTML
                    </button>
                  </div>
                </div>

                {/* Merge Tags Quick Click Bar */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-300">
                      Available Merge Tags (Click to copy):
                    </span>
                    {copiedTag && (
                      <span className="text-[11px] font-bold text-[#0df2a6] flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Copied {copiedTag}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 p-3 rounded-xl bg-[#152a23] border border-[#22493c]">
                    {selectedTemplate.availableMergeTags.map((mt) => (
                      <button
                        key={mt.tag}
                        type="button"
                        onClick={() => copyToClipboard(mt.tag)}
                        title={`Sample: ${mt.sample} (Click to copy)`}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#10231d] hover:bg-[#1e3c32] text-[#0df2a6] border border-[#22493c] text-xs font-mono transition-colors"
                      >
                        <Copy className="w-3 h-3 text-slate-400" />
                        {mt.tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Body Textarea */}
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">
                    Template HTML Content
                  </label>
                  <textarea
                    rows={14}
                    value={selectedTemplate.bodyHtml}
                    onChange={(e) =>
                      setSelectedTemplate({ ...selectedTemplate, bodyHtml: e.target.value })
                    }
                    className="w-full p-4 bg-[#152a23] border border-[#22493c] rounded-xl text-xs font-mono text-white leading-relaxed focus:outline-none focus:border-[#0df2a6]"
                  />
                  <p className="text-[11px] text-slate-400 mt-1.5">
                    HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;table&gt;, &lt;a&gt;, &lt;div&gt; are fully supported and styled to match ReUseIt responsive branding.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Interactive Live Preview (5 cols) */}
            <div className="lg:col-span-5 space-y-4">
              <div className="p-4 rounded-2xl bg-[#10231d] border border-[#22493c] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#0df2a6]" />
                  <span className="text-xs font-black text-white uppercase tracking-wider">
                    Interactive Live Preview
                  </span>
                </div>

                {/* Desktop / Mobile Switcher */}
                <div className="flex items-center gap-1 bg-[#152a23] p-1 rounded-xl border border-[#22493c]">
                  <button
                    type="button"
                    onClick={() => setPreviewMode('desktop')}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors ${
                      previewMode === 'desktop' ? 'bg-[#0df2a6] text-[#08130f]' : 'text-slate-400'
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" /> Desktop
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('mobile')}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors ${
                      previewMode === 'mobile' ? 'bg-[#0df2a6] text-[#08130f]' : 'text-slate-400'
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" /> Mobile
                  </button>
                </div>
              </div>

              {/* Preview Frame Container */}
              <div
                className={`mx-auto transition-all duration-300 rounded-2xl overflow-hidden border border-[#22493c] bg-[#08130f] shadow-2xl ${
                  previewMode === 'mobile' ? 'max-w-[375px]' : 'w-full'
                }`}
              >
                {/* Mock Client Top Bar */}
                <div className="bg-[#152a23] px-4 py-2.5 border-b border-[#22493c] flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-slate-300 truncate">
                    <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                    <span className="font-bold text-white truncate">{selectedTemplate.senderName}</span>
                    <span className="text-[11px] text-slate-400 truncate">&lt;{selectedTemplate.senderAddress}&gt;</span>
                  </div>
                  <span className="text-[10px] text-[#0df2a6] font-mono">LIVE</span>
                </div>

                {/* Rendered HTML in iframe */}
                <iframe
                  title="Email Live Preview"
                  srcDoc={getRenderedPreviewHtml(selectedTemplate)}
                  className="w-full bg-[#08130f] border-0"
                  style={{ height: '620px' }}
                />
              </div>

              {/* Preview Helper Tip */}
              <div className="p-3.5 rounded-xl bg-[#152a23] border border-[#22493c] text-xs text-slate-400 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-[#0df2a6] shrink-0 mt-0.5" />
                <span>
                  Sample merge tags (e.g. <em>{selectedTemplate.availableMergeTags[0]?.tag}</em> →{' '}
                  <strong className="text-white">{selectedTemplate.availableMergeTags[0]?.sample}</strong>)
                  are dynamically substituted in this preview.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SEND LIVE TEST EMAIL MODAL                                                */}
      {/* ========================================================================= */}
      {showTestModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-[#10231d] border border-[#22493c] rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl animate-scaleUp">
            <div className="flex items-start justify-between border-b border-[#22493c] pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0df2a6]/10 border border-[#0df2a6]/20 flex items-center justify-center text-[#0df2a6]">
                  <Send className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Send Live Test Email</h3>
                  <p className="text-xs text-slate-400">{selectedTemplate.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestResult(null);
                }}
                className="text-slate-400 hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5">
                  Target Recipient Email Address
                </label>
                <input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="admin@reuseit.shop"
                  className="w-full px-4 py-2.5 bg-[#152a23] border border-[#22493c] rounded-xl text-sm text-white focus:outline-none focus:border-[#0df2a6]"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Dispatched via <strong>smtp.zeptomail.com:465</strong> from{' '}
                  <span className="text-[#0df2a6]">{selectedTemplate.senderAddress}</span>.
                </p>
              </div>

              {testResult && (
                <div
                  className={`p-4 rounded-xl border text-xs ${
                    testResult.success
                      ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-200'
                      : 'bg-red-950/40 border-red-500/40 text-red-200'
                  }`}
                >
                  <div className="font-bold flex items-center gap-1.5 mb-1">
                    {testResult.success ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-[#0df2a6]" />
                        <span>Transmission Verified Successful!</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-red-400" />
                        <span>Transmission Failed</span>
                      </>
                    )}
                  </div>
                  {testResult.messageId && (
                    <p className="font-mono text-[11px] text-slate-300">
                      Message ID: {testResult.messageId}
                    </p>
                  )}
                  {testResult.error && <p>{testResult.error}</p>}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowTestModal(false);
                  setTestResult(null);
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-[#152a23] hover:bg-[#1c382f] text-slate-300 border border-[#22493c] transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSendTest}
                disabled={sendingTest || !testEmailAddress}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold bg-[#0df2a6] hover:bg-[#0bd390] text-[#08130f] transition-colors shadow-glow-sm disabled:opacity-50"
              >
                <Send className={`w-3.5 h-3.5 ${sendingTest ? 'animate-spin' : ''}`} />
                {sendingTest ? 'Dispatching via ZeptoMail...' : 'Send Live Test Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ZOHO ZEPTOMAIL SENDER ADDRESSES GUIDE MODAL                                */}
      {/* ========================================================================= */}
      {showSenderGuide && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-[#10231d] border border-[#22493c] rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl animate-scaleUp">
            <div className="flex items-start justify-between border-b border-[#22493c] pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Zoho ZeptoMail Verified Senders Guide</h3>
                  <p className="text-xs text-slate-400">Required Senders for Domain: mail.reuseit.shop</p>
                </div>
              </div>
              <button
                onClick={() => setShowSenderGuide(false)}
                className="text-slate-400 hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs text-slate-300">
              <p>
                In your <strong>Zoho ZeptoMail Dashboard</strong> under <em>Mail Agents &gt; Senders / From Addresses</em> for <strong>mail.reuseit.shop</strong>, add and verify the following 6 operational email addresses:
              </p>

              <div className="space-y-2.5">
                {VERIFIED_ZEPTO_SENDERS.map((sender) => (
                  <div
                    key={sender.address}
                    className="p-3 rounded-xl bg-[#152a23] border border-[#22493c] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <div>
                      <div className="font-mono font-bold text-[#0df2a6] text-xs">
                        {sender.address}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        Display Name: <span className="text-white font-medium">{sender.name}</span>
                      </div>
                      <div className="text-[11px] text-slate-400">
                        Operational Flow: {sender.purpose}
                      </div>
                    </div>
                    <span className="self-start sm:self-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30">
                      Required
                    </span>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-[#08130f] border border-[#22493c] space-y-2">
                <div className="font-bold text-white flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#0df2a6]" />
                  Active SMTP Credentials
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
                  <div>Host: <span className="text-white">smtp.zeptomail.com</span></div>
                  <div>Port: <span className="text-white">465 (SSL)</span></div>
                  <div>Auth User: <span className="text-white">emailapikey</span></div>
                  <div>Domain: <span className="text-white">mail.reuseit.shop</span></div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowSenderGuide(false)}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-[#0df2a6] text-[#08130f] hover:bg-[#0bd390] transition-colors"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
